<script setup>
// Barra de pausa dos dois discadores (Asterisk e WhatsApp SIP). Mesma pausa da
// barra superior e do menu de status (store agentPauses via useAgentPause):
// pausar/retomar troca a disponibilidade (Ocupado ⇄ Online) e o servidor faz o
// resto — Relatório de Pausas e pausa nas filas. Passar do limite do motivo
// não encerra nada: a barra fica vermelha e o agente segue pausado.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAgentPause } from 'dashboard/components-next/agent-pause/useAgentPause';

defineProps({
  isConnected: { type: Boolean, default: false },
});

const { t } = useI18n();
const {
  options: pauseReasons,
  isPaused,
  label,
  clock,
  limitLabel,
  limitTooltip,
  isOverdue,
  isChanging,
  pause,
  resume,
  optionLimitLabel,
} = useAgentPause();

const isPauseMenuOpen = ref(false);

const selectPauseReason = reason => {
  isPauseMenuOpen.value = false;
  if (isChanging.value) return;
  pause(reason);
};
</script>

<template>
  <div
    class="relative flex items-center justify-between gap-2 px-2 py-1.5 border-b border-n-weak flex-shrink-0"
    :class="{
      'bg-n-amber-3 dark:bg-n-amber-9/20': isPaused && !isOverdue,
      'bg-n-ruby-3 dark:bg-n-ruby-9/20': isPaused && isOverdue,
    }"
  >
    <template v-if="isPaused">
      <!-- 2 linhas: o discador tem ~250px, e motivo + cronômetro + botão
           numa linha só truncavam o motivo em "Em pau…" -->
      <span
        class="flex items-center gap-1.5 min-w-0 text-xs"
        :class="isOverdue ? 'text-n-ruby-11' : 'text-n-amber-11'"
      >
        <span
          class="size-3.5 flex-shrink-0"
          :class="isOverdue ? 'i-lucide-alarm-clock' : 'i-lucide-pause-circle'"
        />
        <span class="flex flex-col min-w-0">
          <span class="truncate font-medium">{{ label }}</span>
          <span
            v-tooltip="limitTooltip"
            class="font-mono text-[11px] tabular-nums"
            :class="{ 'font-semibold': isOverdue }"
          >
            {{ clock }}
            <template v-if="limitLabel">/ {{ limitLabel }}</template>
          </span>
        </span>
      </span>
      <Button
        size="xs"
        variant="faded"
        :color="isOverdue ? 'ruby' : 'amber'"
        class="flex-shrink-0"
        :is-loading="isChanging"
        :disabled="!isConnected"
        @click="resume"
      >
        {{ t('AGENT_PAUSE.BAR.RESUME') }}
      </Button>
    </template>
    <template v-else>
      <span class="flex-1" />
      <Button
        size="xs"
        variant="ghost"
        color="slate"
        icon="i-lucide-chevron-down"
        trailing-icon
        class="flex-shrink-0"
        :is-loading="isChanging"
        :disabled="!isConnected || isChanging"
        @click="isPauseMenuOpen = !isPauseMenuOpen"
      >
        {{ t('AGENT_PAUSE.BAR.PAUSE') }}
      </Button>
      <template v-if="isPauseMenuOpen">
        <div class="fixed inset-0 z-10" @click="isPauseMenuOpen = false" />
        <div
          class="absolute z-20 ltr:right-2 rtl:left-2 top-full mt-1 w-56 max-h-80 overflow-y-auto py-1 rounded-lg bg-n-solid-2 border border-n-weak shadow-lg"
        >
          <p
            class="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ t('AGENT_PAUSE.BAR.SELECT_REASON') }}
          </p>
          <p
            v-if="!pauseReasons.length"
            class="px-3 py-1.5 text-xs text-n-slate-11"
          >
            {{ t('AGENT_PAUSE.BAR.NO_REASONS') }}
          </p>
          <button
            v-for="reason in pauseReasons"
            :key="reason.value"
            type="button"
            class="flex items-center justify-between gap-2 w-full px-3 py-1.5 text-left text-xs text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            @click="selectPauseReason(reason.value)"
          >
            <span class="truncate">{{ reason.label }}</span>
            <span
              v-if="optionLimitLabel(reason)"
              class="flex-shrink-0 text-[10px] tabular-nums text-n-slate-10"
            >
              {{ optionLimitLabel(reason) }}
            </span>
          </button>
        </div>
      </template>
    </template>
  </div>
</template>
